import { useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageCircle, Send, ChevronDown, HelpCircle } from "lucide-react";
import useSiteSettings from "@/hooks/useSiteSettings";

const ContactPage = () => {
  const settings = useSiteSettings();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post("/contact", formData);
      toast.success("Your message has been sent successfully!");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    }

    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const faqs = [
    {
      question: "Which frameworks do the scripts support?",
      answer: "Compatibility is listed on each product page. Check the stated framework and dependencies before buying."
    },
    {
      question: "Are updates free after purchase?",
      answer: "Update terms are listed with each product. Contact support if you need to confirm compatibility before buying."
    },
    {
      question: "Do you provide installation support?",
      answer: "Available installation help and documentation are described on each product page. You can also use the contact form."
    },
    {
      question: "Do you do custom script development?",
      answer: "Use the contact form with your server framework and requirements, and we will confirm whether the work is a fit."
    }
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="hero-glow opacity-30" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-xs uppercase tracking-widest text-primary mb-4 block">
              Contact
            </span>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              Get in Touch
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Reach out to us with your questions or suggestions. We'll respond as soon as possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-bold tracking-tight uppercase mb-6">
                Send a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                      Your Name
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-name"
                      className="bg-slate-950/50 border-slate-800 focus:border-primary rounded-lg h-12"
                      placeholder="Enter your name"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                      Email
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email"
                      className="bg-slate-950/50 border-slate-800 focus:border-primary rounded-lg h-12"
                      placeholder="Your email address"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Subject
                  </label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    data-testid="contact-subject"
                    className="bg-slate-950/50 border-slate-800 focus:border-primary rounded-lg h-12"
                    placeholder="Message subject"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Your Message
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    data-testid="contact-message"
                    className="bg-slate-950/50 border-slate-800 focus:border-primary rounded-lg min-h-[150px] resize-none"
                    placeholder="Write your message..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg border-l-4 border-accent uppercase tracking-widest font-bold py-6 glow-blue-hover"
                >
                  {loading ? (
                    <span className="loading-spinner w-5 h-5" />
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Contact Info & FAQ */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-12"
            >
              {settings?.discord_url && (
                <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold tracking-wide mb-2">Our Discord Server</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Join our Discord server for the fastest support and community interaction.
                    </p>
                    <a href={settings.discord_url} target="_blank" rel="noopener noreferrer">
                      <Button
                        data-testid="discord-btn"
                        variant="outline"
                        className="rounded-lg border-primary/30 text-primary hover:bg-primary/10"
                      >
                        Join Discord
                      </Button>
                    </a>
                  </div>
                </div>
                </div>
              )}

              {/* FAQ */}
              <div>
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4" />
                  Frequently Asked Questions
                </h3>
                <div className="space-y-3">
                  {faqs.map((faq, index) => (
                    <div
                      key={index}
                      className="border border-slate-800 rounded-lg bg-slate-950/50 hover:border-slate-700 transition-colors"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-4 text-left"
                        data-testid={`faq-${index}`}
                      >
                        <span className="font-medium text-sm">{faq.question}</span>
                        <ChevronDown
                          className={`w-4 h-4 text-primary transition-transform ${
                            openFaq === index ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {openFaq === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="px-4 pb-4"
                        >
                          <p className="text-sm text-muted-foreground">{faq.answer}</p>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
